/* eslint-disable react-hooks/exhaustive-deps */
import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import { StylesTooltip, WizardTooltipPopupGlobalStyles } from './wizardTooltip.styles';
import { CloseButton } from '@chakra-ui/react';

interface Step {
  target: string | string[];
  title: string;
  description: string | ReactNode;
  placement?: string;
  offsetExtraY?: number;
  offsetExtraX?: number;
}

export interface WizardComponentProps {
  id: string;
  steps: Step;
  initialized: boolean;
  setInitialized: (arg1: boolean) => void;
  container?: string;
  width?: number;
  height?: number;
  offset?: number;
  onFinish?: () => void;
  isModal?: boolean;
  color?: string;
  bgColor?: string;
  stylesTooltip?: StylesTooltip;
}

interface IFixedCoordinates {
  top?: number | string;
  bottom?: number | string;
  left?: number | string;
  right?: number | string;
}

const WizardTooltipComponent: React.FC<WizardComponentProps> = ({
  id,
  steps,
  container = 'body',
  width = 294,
  height = 260,
  offset = 20,
  onFinish,
  isModal = true,
  initialized,
  setInitialized,
  color,
  bgColor='black',
  stylesTooltip
}) => {
  const [tooltipPosition, setTooltipPosition] = useState<IFixedCoordinates>({
    top: 'auto',
    left: 'auto',
    bottom: 'auto',
    right: 'auto',
  });

  const {
    target,
    title,
    description,
    placement = 'top',
    offsetExtraY = 0,
    offsetExtraX = 0,
  } = steps;


  const handleOnFinish = () => {
    removeWizardFocus();
    setTooltipPosition({
      top: 'auto',
      left: '-3000px',
      bottom: 'auto',
      right: 'auto',
    });
    const containerWizard =
      document?.querySelector(`${container}`) || document?.body;
    containerWizard.classList.remove(`wizard--active-${id}`);
    setInitialized(false);
  };

  const clickOnFinish = () => {
    handleOnFinish();
    onFinish && onFinish();
  };

  const removeWizardFocus = () => {
    if (Array.isArray(target)) {
      target.forEach((val) => {
        const targetId = document?.querySelector(`#${val}`);
        targetId?.classList.remove(`wizard--focus-${id}`);
        targetId?.classList.remove(`wizard--focus-${id}--white`);
      });
    } else {
      const targetId = document?.querySelector(`#${target}`);
      targetId?.classList.remove(`wizard--focus-${id}`);
      targetId?.classList.remove(`wizard--focus-${id}--white`);
    }
  };

  const tooltipSize = { width, height };

  /**
   * Added window dimension values as state so it can be observed
   * by the component useEffect and be re-rendered accordingly
   */
  const [windowDimensions, setWindowDimensions] = React.useState({
    height: window.innerHeight,
    width: window.innerWidth,
  });

  const handleResize = () => {
    setWindowDimensions({
      height: window.innerHeight,
      width: window.innerWidth,
    });
  };

  /**
   * This is so when attaching the eventListener to the window resize,
   * the function doesn't get called at every pixel change
   *
   * @param fn Function to debounce
   * @param ms Delay in miliseconds
   * @returns Debounced function
   */
  const debounce = (fn: () => void, ms: number) => {
    let timer: number | null | NodeJS.Timeout;
    return () => {
      clearTimeout(timer as number);
      timer = setTimeout(() => {
        timer = null;
        fn();
      }, ms);
    };
  };

  const debouncedHandleResize = useCallback(() => debounce(handleResize, 100) , [])

  /**
   * Add event listener when resizing the browser
   * and remove it when component is dismounted
   */
  useEffect(() => {
    window.addEventListener('resize', debouncedHandleResize());
    return () => {
      window.removeEventListener('resize', debouncedHandleResize());
    };
  }, []);

  useEffect(() => {
    const containerWizard =
      document?.querySelector(`${container}`) || document?.body;
    if (initialized) {
      containerWizard.classList.add(`wizard--active-${id}`);
    } else {
      containerWizard.classList.remove(`wizard--active-${id}`);
    }
  }, [initialized]);

  const defaultTarget = {
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  };

  const applyFocusStyles = (target: string | any[], id: string) => {
    let targetElement;

    if (Array.isArray(target)) {
      targetElement = document.getElementById(target[0]);
      target.forEach((val) => {
        document.getElementById(val)?.classList.add(`wizard--focus-${id}`);
      });
    } else {
      targetElement = document.getElementById(target);
      targetElement?.classList.add(`wizard--focus-${id}`);
    }

    if (targetElement) {
      applyWhiteBackgroundStyles(target, id, targetElement);
      positionTooltip(targetElement, id);
    }
  };

  const applyWhiteBackgroundStyles = (
    target: string | any[],
    id: string,
    targetElement: Element
  ) => {
    if (Array.isArray(target)) {
      target.forEach((val) => {
        const targetStyle = getComputedStyle(
          document.getElementById(val) as HTMLElement
        );
        if (
          !targetStyle.backgroundColor ||
          targetStyle.backgroundColor === 'rgba(0, 0, 0, 0)' ||
          targetStyle.backgroundColor === 'transparent'
        ) {
          document
            .getElementById(val)
            ?.classList.add(`wizard--focus-${id}--white`);
        }
      });
    } else {
      const targetStyle = getComputedStyle(targetElement);
      if (
        !targetStyle.backgroundColor ||
        targetStyle.backgroundColor === 'rgba(0, 0, 0, 0)' ||
        targetStyle.backgroundColor === 'transparent'
      ) {
        targetElement?.classList.add(`wizard--focus-${id}--white`);
      }
    }
  };

  const positionTooltip = (targetElement: HTMLElement, id: string) => {
    const tooltipHeight = height;
    const tooltipWidth = width;

    const elementPos = targetElement?.getBoundingClientRect() || defaultTarget;
    let offSetX = 0;
    let offSetY = 0;
    
    const scrollY = window.scrollY || window.pageYOffset

    switch (placement) {
      case 'top':
      case 'top-start':
      case 'top-end':   
        offSetX =
          elementPos.width / 2 +
          elementPos.left -
          tooltipWidth / 2 -
          offsetExtraX;
        offSetY =
          elementPos.top +
          scrollY +
          elementPos.height +
          offset -
          offsetExtraY;
        break;

      case 'bottom':
      case 'bottom-start':
      case 'bottom-end':
        offSetX =
          elementPos.width / 2 +
          elementPos.left -
          tooltipWidth / 2 -
          offsetExtraX;
        offSetY =
          elementPos.top +
          window.scrollY -
          tooltipHeight -
          offset -
          offsetExtraY;
        break;
  
      case 'left':
        offSetX = elementPos.width + elementPos.left + offset + offsetExtraX;
        offSetY =
          elementPos.top +
          scrollY -
          tooltipHeight / 2 +
          elementPos.height / 2 -
          offsetExtraY;
        break;

      case 'right':
        offSetX = elementPos.left - tooltipWidth - offset + offsetExtraX;
        offSetY =
          elementPos.top +
          scrollY -
          tooltipHeight / 2 +
          elementPos.height / 2 -
          offsetExtraY;
        break;

      default:
        offSetX =
          elementPos.width / 2 +
          elementPos.left -
          tooltipWidth / 2 -
          offsetExtraX;
        offSetY =
          elementPos.top -
          scrollY +
          elementPos.height +
          tooltipHeight +
          offset -
          offsetExtraY;
        break;
    }

    setTooltipPosition({
      top: 'auto', //top: offSetY > 0 ? offSetY : 0,
      left: offSetX > 0 ? offSetX : 0,
      bottom: 100, //bottom: 'auto',
      right: 'auto', //right: 'auto',
    });

    const scrollTop = elementPos.top + window.pageYOffset - tooltipHeight - offset;

    window.scrollTo({ top: scrollTop, behavior: 'smooth' }); // scroll to highlighted element
  };

  useEffect(() => {
    applyFocusStyles(target, id);
  }, [
    container,
    id,
    steps,
    width,
    height,
    offset,
    offsetExtraY,
    offsetExtraX,
    tooltipPosition.top,
    tooltipPosition.bottom,
    tooltipSize.width,
    tooltipSize.height,
    placement,
    target,
    windowDimensions,
  ]);

  return (
    <>
      <WizardTooltipPopupGlobalStyles
        wizardID={id}
        placement={placement}
        container={container}
        isModal={isModal}
        color={color}
        bgColor={bgColor}
        stylesTooltip={stylesTooltip}
        windowDimensions={windowDimensions}
      />
      <div className={`wizard-${id}`} data-testid="wizard">
        <div
          className={`tooltip-${id}`}
          data-testid="wizard__tooltip"
          style={{
            width: `${tooltipSize.width}px`,
            height: `${tooltipSize.height}px`,
            top: tooltipPosition.top,
            left: tooltipPosition.left,
            right: tooltipPosition.right,
            bottom: tooltipPosition.bottom,
          }}
        >
          <div className={`tooltip-body-${id}`}>
            <div className={`tooltip-header-title-${id}`} >
              <p className="tooltip-body-title" data-testid="wizard__title">
                {title}
              </p>
              <CloseButton
                size={'md'}
                variant={'light'}
                onClick={clickOnFinish}
                data-testid="wizard__closeButton"
            />
            </div>
            <p className="tooltip-body-desc" data-testid="wizard__description">
              {description}
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default WizardTooltipComponent;
